import React, { useMemo, useState } from 'react';

import { TableContainer, Table, Thead, Tr, Th, Tbody, Skeleton, Icon, Text, HStack } from '@ecoinvent/ui';
import mixpanel from 'mixpanel-browser';
import { BiSortAlt2, BiSortDown, BiSortUp } from 'react-icons/bi';

import NoResultCard from 'components/UI/NoResultCard';
import useLciaScores from 'hooks/data/datasets/lcia/useLciaScores';
import { RecentMethod } from 'hooks/useSavedLciaMethod/types';
import { useDatasetSession } from 'pages/Dataset/DatasetSessionContext';
import { MIXPANEL_EVENTS } from 'utilities/constants/mixpanel';

import { SortDirection, sortDirections, SortField, sortFields } from './constants';
import ImpactAssessmentRow from './ImpactAssessmentRow';

type Props = {
  isLoading?: boolean;
  spoldId: number;
  selectedMethod: RecentMethod;
  hideScores?: boolean;
};

const ImpactAssessmentTable = ({ isLoading, spoldId, selectedMethod, hideScores }: Props) => {
  const { data: lciaScores, isLoading: areScoresLoading } = useLciaScores(spoldId, selectedMethod.method_id);
  const [sortField, setSortField] = useState<SortField>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const { increment } = useDatasetSession();

  const isAnythingLoading = isLoading || areScoresLoading;

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortDirection === sortDirections.ASC) {
        setSortDirection(sortDirections.DESC);
      } else {
        setSortDirection(null);
        setSortField(null);
      }
    } else {
      setSortField(field);
      setSortDirection(sortDirections.ASC);
    }
    mixpanel.track(MIXPANEL_EVENTS.IMPACT_ASSESSMENT_TABLE_SORTED, {
      spold_id: spoldId,
      lcia_method: selectedMethod.method_name,
      sort_field: field,
      sort_direction: sortField === field ? (sortDirection === sortDirections.ASC ? 'DESC' : null) : 'ASC',
    });
    increment('impact_assessment_action_count');
  };

  const sortedScores = useMemo(() => {
    if (!lciaScores || !sortField || !sortDirection) {
      return lciaScores;
    }

    const sorted = [...lciaScores].sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (sortField) {
        case sortFields.CATEGORY:
          aValue = a.category_name.toLowerCase();
          bValue = b.category_name.toLowerCase();
          break;
        case sortFields.INDICATOR:
          aValue = a.indicator_name.toLowerCase();
          bValue = b.indicator_name.toLowerCase();
          break;
        case sortFields.SCORE:
          aValue = a.amount;
          bValue = b.amount;
          break;
        case sortFields.UNIT:
          aValue = a.unit_name.toLowerCase();
          bValue = b.unit_name.toLowerCase();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === sortDirections.ASC ? -1 : 1;
      if (aValue > bValue) return sortDirection === sortDirections.DESC ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [lciaScores, sortField, sortDirection]);

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <Icon as={BiSortAlt2} h="16px" w="16px" />;
    }
    if (sortDirection === sortDirections.ASC) {
      return <Icon as={BiSortUp} h="16px" w="16px" />;
    }
    if (sortDirection === sortDirections.DESC) {
      return <Icon as={BiSortDown} h="16px" w="16px" />;
    }
    return <Icon as={BiSortAlt2} h="16px" w="16px" />;
  };

  return (
    <Skeleton isLoaded={!isLoading}>
      <TableContainer whiteSpace={'break-spaces'}>
        <Table size="sm">
          <Thead>
            <Tr>
              <Th pl={4} cursor="pointer" onClick={() => handleSort('category')} userSelect="none">
                <HStack spacing={1}>
                  <Text pt={0.5}>Impact category</Text>
                  {getSortIcon('category')}
                </HStack>
              </Th>
              <Th align="left" cursor="pointer" onClick={() => handleSort('indicator')} userSelect="none">
                <HStack spacing={1}>
                  <Text pt={0.5}>Indicator</Text>
                  {getSortIcon('indicator')}
                </HStack>
              </Th>
              <Th
                isNumeric
                whiteSpace={'nowrap'}
                cursor={hideScores ? 'default' : 'pointer'}
                onClick={hideScores ? undefined : () => handleSort('score')}
                userSelect="none"
              >
                <HStack spacing={1}>
                  <Text pt={0.5}>Impact Score</Text>
                  {!hideScores && getSortIcon('score')}
                </HStack>
              </Th>
              <Th align="left" cursor="pointer" onClick={() => handleSort('unit')} userSelect="none">
                <HStack spacing={1}>
                  <Text pt={0.5}>Unit</Text>
                  {getSortIcon('unit')}
                </HStack>
              </Th>
              <Th align="left" />
            </Tr>
          </Thead>
          {!isAnythingLoading && sortedScores && sortedScores.length > 0 && (
            <Tbody>
              {sortedScores.map((row) => {
                return (
                  <ImpactAssessmentRow selectedMethod={selectedMethod} key={row.index} score={row} hideScores={hideScores} />
                );
              })}
            </Tbody>
          )}
          {!isLoading && lciaScores?.length === 0 && (
            <Tbody>
              <NoResultCard>No Results Found</NoResultCard>
            </Tbody>
          )}
        </Table>
      </TableContainer>
    </Skeleton>
  );
};

export default ImpactAssessmentTable;
